import { useEffect, type CSSProperties } from 'react';

import type { VersionNodeSummary } from '../../services/cplex';
import { VersionMetaList } from './VersionMetaList';

// Shows one version's full agent-authored detail — its summary and the list of
// constraints / assumptions it added — opened from the version switcher so a
// version is more than its title.

interface Props {
  version: VersionNodeSummary;
  onClose: () => void;
}

const OVERLAY: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
};
const CARD: CSSProperties = {
  background: '#fff',
  color: '#1f2937',
  borderRadius: 12,
  width: 'min(560px, 94vw)',
  maxHeight: '82vh',
  overflow: 'auto',
  boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
};

export function VersionDetailsModal({ version, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      style={OVERLAY}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Version details"
    >
      <div style={CARD} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <strong style={{ fontSize: 14 }}>
            {version.label}{' '}
            <span style={{ color: '#64748b', fontWeight: 400 }}>[{version.state}]</span>
          </strong>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              color: '#64748b',
              fontSize: 20,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
            Constraints &amp; assumptions
          </div>
          <VersionMetaList dynamicMetadata={version.dynamic_metadata} />
        </div>
      </div>
    </div>
  );
}
